import { Title } from "@solidjs/meta";
import {
  createSignal,
  createMemo,
  createEffect,
  Show,
  Switch,
  Match,
  For,
  on,
  onCleanup,
  onMount,
  untrack,
} from "solid-js";
import type {
  Location,
  AppointmentTemplates,
  FormData,
  Step,
  PatientType,
  ConfirmationData,
  Provider,
  ReferralQuestion,
} from "./types";
import {
  rememberLanguagePick,
  reportTestConversion,
  reportTrackingTestVisit,
  submitSchedule,
  type TestConversionSinks,
} from "./api";
import { PatientTypeStep } from "./_patient_type_step";
import { AppointmentInfoStep } from "./_appointment_info_step";
import { ProviderStep } from "./_provider_step";
import { AppointmentTimeStep } from "./_appointment_time_step";
import { PatientInfoStep } from "./_patient_info_step";
import { InsuranceInfoStep } from "./_insurance_info_step";
import { ReviewStep } from "./_review_step";
import { DepositStep } from "./_deposit_step";
import { ConfirmationStep } from "./_confirmation_step";
import { StepIndicator } from "./_step_indicator";
import { I18nProvider, type TFunction, createT, dateLocale } from "./i18n";
import { localeDate, localeTime } from "~/util/date/intl_dates";
import { type PatientI18n, PatientTextProvider, createPatientText } from "~/patient_i18n/i18n";
import { PatientLanguageBar } from "~/patient_i18n/language_bar";
import { embedParentReceives } from "~/common/public_form/embed_messaging";
import { PublicFormShell } from "~/common/public_form/shell";
import { PublicFormHeader } from "~/common/public_form/header";
import {
  GOOGLE_TAG_MANAGER,
  META_PIXEL,
  TrackingPixels,
  trackingPixelLoaded,
  type TrackingPixelConfig,
} from "~/common/tag_scripts";
import { Button } from "~/common/button";
import { Icon } from "~/icon";
import { SEPARATOR_DOT } from "~/util/html/codepoint";
import { emitBookingComplete, emitBookingStep } from "./analytics";

type Props = {
  location: Location;
  appointmentTemplates: AppointmentTemplates;
  trackingPixels: TrackingPixelConfig[];
  embedOrigins: string[];
  marketingClickId: string | null;
  phoneVerificationEnabled: boolean;
  /** False where the office raises no verification requests; the insurance step is skipped. */
  insuranceInfoEnabled: boolean;
  /** The insurance step also takes card photos. */
  insuranceCardUploadEnabled: boolean;
  slotHoldsEnabled: boolean;
  stripePublishableKey: string | null;
  /** Set only when this location surcharges card payments. */
  cardFeePercentage: number | null;
  /** Set when the visit came from a live Marketing tracking test link; the page then books nothing. */
  trackingTest: { code: string } | null;
  referralQuestion: ReferralQuestion;
  /** The patient pages' language (PatientPageLocale): the page's locale, the menu, the browser's offer. */
  patientI18n: PatientI18n;
};

// Tracking test mode is seen only by practice staff and their agency, so its copy stays in
// English rather than going through t().
const TRACKING_TEST_REFUSAL =
  "This booking page is in tracking test mode, so it can't book a real appointment.";
// Long enough for the location's GTM container and Meta Pixel to load after the page does.
const TAG_SETTLE_MS = 3000;

const INITIAL_FORM_DATA: FormData = {
  patientType: null,
  appointmentTemplateId: null,
  chiefComplaint: "",
  providerId: null,
  appointmentDate: new Date().toISOString().split("T")[0]!,
  appointmentSlotKey: null,
  appointmentSlotProviderName: null,
  // Deliberately not cleared with appointmentSlotKey: the token is what lets a
  // patient re-pick the same time without being locked out by their own hold.
  holdId: null,
  waitlist: false,
  firstName: "",
  lastName: "",
  preferredName: "",
  dob: "",
  phone: "",
  email: "",
  referredFromSelection: "",
  referredFromDetail: "",
  hasInsurance: null,
  insuranceChanged: null,
  insuranceName: "",
  insuranceSubscriberName: "",
  insuranceSubscriberDob: "",
  insuranceSubscriberId: "",
  insuranceGroupNumber: "",
  insuranceCardFront: null,
  insuranceCardBack: null,
  phoneVerificationCode: "",
  phoneVerificationId: crypto.randomUUID(),
  codeSentPhones: [],
  verifiedPhones: [],
  marketingClickId: null,
  // A reload after a pick keeps it: the menu wrote the marker into the address.
  languagePicked: new URLSearchParams(window.location.search).has("language_pick"),
};

const CHROMELESS_STEPS: Step[] = ["patient_type", "review", "deposit", "confirmation"];

export default function ScheduleForm(props: Props) {
  // The language changes in place, so a pick halfway through keeps everything the patient typed
  // (and their hold on a time). Every string below reads it through t() or locale().
  const [locale, setLocale] = createSignal(untrack(() => props.patientI18n.locale));
  const patientText = untrack(() => createPatientText(props.patientI18n));
  const t: TFunction = (key, vars) => createT(locale())(key, vars);

  const [step, setStep] = createSignal<Step>("patient_type");
  const [formData, setFormData] = createSignal<FormData>(
    untrack(() => ({
      ...INITIAL_FORM_DATA,
      appointmentDate: new Intl.DateTimeFormat("en-CA", {
        timeZone: props.location.timezone,
      }).format(new Date()),
      marketingClickId: props.marketingClickId,
    })),
  );
  const [confirmation, setConfirmation] = createSignal<ConfirmationData | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [submitErrors, setSubmitErrors] = createSignal<string[]>([]);

  // Cache provider list between steps
  const [providers, setProviders] = createSignal<Provider[]>([]);

  const update = (partial: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  // The practice's approved wording in the page's language, else its English.
  // Its own memo, so typing elsewhere in the form doesn't rebuild the options (and drop focus).
  const patientType = createMemo(() => formData().patientType);
  const templates = createMemo(() => {
    const pt = patientType();
    const offered =
      pt === "new_patient"
        ? props.appointmentTemplates.newPatient
        : pt === "existing_patient"
          ? props.appointmentTemplates.existingPatient
          : [];
    const current = locale();
    return offered.map((template) => ({
      ...template,
      name: template.nameTranslations[current] ?? template.name,
      bookingInstructions:
        template.bookingInstructionsTranslations[current] ?? template.bookingInstructions,
    }));
  });

  // Picks go out one after another, so the last one is the one the cookie keeps.
  let pickSaved = Promise.resolve();
  const pickLanguage = (code: string) => {
    setLocale(code);
    update({ languagePicked: true });
    pickSaved = pickSaved.then(() => rememberLanguagePick(code));
    // A reload keeps the pick, as a menu pick on any patient page does (PatientLanguagePick).
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("locale", code);
      url.searchParams.set("language_pick", "1");
      // A public Inertia page without Turbo, so the url helpers don't apply here.
      // eslint-disable-next-line no-restricted-syntax
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      /* a sandboxed frame may refuse; the pick still holds for this visit */
    }
  };

  createEffect(() => {
    document.documentElement.lang = locale();
  });

  const selectedTemplate = createMemo(() => {
    const id = formData().appointmentTemplateId;
    return templates().find((t) => t.id === id) ?? null;
  });

  const selectedProvider = createMemo(() => {
    const id = formData().providerId;
    return providers().find((p) => p.id === id) ?? null;
  });

  // Steps that stand on their own: the first (nothing picked yet), and the three
  // that already restate the whole booking. Everything else gets the step
  // indicator and the running summary.
  const showsChrome = () => !CHROMELESS_STEPS.includes(step());

  // Server-gated: the prop is only ever set when this location is actually
  // taking deposits (OnlineScheduling::DepositCheck), so there is nothing to
  // re-check here.
  const depositCents = createMemo(() => selectedTemplate()?.depositAmountCents ?? null);

  const selectionSummary = createMemo(() => {
    const rows: { label: string; value: string }[] = [];
    const fd = formData();
    const currentStep = step();

    const tmpl = selectedTemplate();
    if (tmpl) rows.push({ label: t("type_label"), value: tmpl.name });

    const pastProvider = ["appointment_time", "patient_info", "insurance_info", "review"].includes(
      currentStep,
    );
    if (pastProvider) {
      const prov = selectedProvider();
      const value = prov?.name ?? fd.appointmentSlotProviderName ?? t("any_provider");
      rows.push({ label: t("provider"), value });
    }

    if (
      fd.appointmentSlotKey &&
      ["patient_info", "insurance_info", "review"].includes(currentStep)
    ) {
      const parts = fd.appointmentSlotKey.split("::");
      if (parts.length >= 2) {
        const dt = new Date(parts[1]!);
        const tz = props.location.timezone;
        const dl = dateLocale(locale());
        const dateStr = localeDate(dl, dt, {
          weekday: "short",
          month: "short",
          day: "numeric",
          timeZone: tz,
        });
        const timeStr = localeTime(dl, dt, {
          hour: "numeric",
          minute: "2-digit",
          timeZone: tz,
        });
        rows.push({ label: t("time"), value: `${dateStr}, ${timeStr}` });
      }
    }

    return rows;
  });

  const stepsForFlow = createMemo(() => {
    const pt = formData().patientType;
    if (pt === "new_patient") {
      return [
        { id: "appointment_info" as const, label: t("step_appointment") },
        { id: "provider_select" as const, label: t("step_provider") },
        { id: "appointment_time" as const, label: t("step_time") },
        { id: "patient_info" as const, label: t("step_your_info") },
        ...(props.insuranceInfoEnabled
          ? [{ id: "insurance_info" as const, label: t("step_insurance") }]
          : []),
      ];
    }
    if (pt === "existing_patient") {
      return [
        { id: "existing_patient_info" as const, label: t("step_your_info") },
        { id: "appointment_info" as const, label: t("step_appointment") },
        { id: "provider_select" as const, label: t("step_provider") },
        { id: "appointment_time" as const, label: t("step_time") },
        ...(props.insuranceInfoEnabled
          ? [{ id: "insurance_info" as const, label: t("step_insurance") }]
          : []),
      ];
    }
    return [];
  });

  // Canonical step order for the current patient-type flow. Single source of
  // truth for navigation and the booking_step funnel index below.
  const flowOrder = createMemo<Step[]>(() => {
    const pt = formData().patientType;
    // Paying is the last thing that happens, so nothing about the booking can
    // change between the quote and the charge.
    const tail: Step[] = depositCents() ? ["review", "deposit"] : ["review"];
    if (pt === "new_patient") {
      return [
        "patient_type",
        "appointment_info",
        "provider_select",
        "appointment_time",
        "patient_info",
        ...(props.insuranceInfoEnabled ? (["insurance_info"] as Step[]) : []),
        ...tail,
      ];
    }
    if (pt === "existing_patient") {
      return [
        "patient_type",
        "existing_patient_info",
        "appointment_info",
        "provider_select",
        "appointment_time",
        ...(props.insuranceInfoEnabled ? (["insurance_info"] as Step[]) : []),
        ...tail,
      ];
    }
    return ["patient_type"];
  });

  // The server refuses a test-mode booking too; stopping here also keeps a deposit from being taken.
  const refuseInTestMode = () => {
    if (!props.trackingTest) return false;
    setSubmitErrors([TRACKING_TEST_REFUSAL]);
    return true;
  };

  // Review's action when a deposit is due. A deposit rides on the slot
  // reservation, so without one there is nothing to pay against — send the
  // patient back to pick a time rather than into a step that can only refuse.
  const goToPayment = () => {
    if (refuseInTestMode()) return;
    if (!formData().holdId) {
      setSubmitErrors([t("deposit_needs_hold")]);
      return;
    }
    setSubmitErrors([]);
    setStep("deposit");
  };

  const goNext = () => {
    const current = step();
    const order = flowOrder();
    const idx = order.indexOf(current);
    if (idx < 0 || idx >= order.length - 1) return;

    if (current === "provider_select") {
      const today = new Intl.DateTimeFormat("en-CA", {
        timeZone: props.location.timezone,
      }).format(new Date());
      update({
        appointmentDate: today,
        appointmentSlotKey: null,
        appointmentSlotProviderName: null,
      });
    }

    setStep(order[idx + 1]!);
  };

  const goBack = () => {
    const order = flowOrder();
    const idx = order.indexOf(step());
    if (idx > 0) setStep(order[idx - 1]!);
  };

  // Notify the embedding parent page on each step view so its tag manager can
  // build a booking funnel (where patients drop off). Step is the sole reactive
  // dependency; the confirmation step is covered by booking_complete on submit.
  createEffect(() => {
    const current = step();
    if (current === "confirmation") return;
    const idx = untrack(flowOrder).indexOf(current);
    emitBookingStep({
      step: idx >= 0 ? idx + 1 : 0,
      stepName: current,
      embedOrigins: props.embedOrigins,
    });
  });

  const handlePatientTypeSelect = (type: PatientType) => {
    // Reset downstream form data when switching patient type
    update({
      patientType: type,
      appointmentTemplateId: null,
      chiefComplaint: "",
      providerId: null,
      appointmentSlotKey: null,
      appointmentSlotProviderName: null,
      waitlist: false,
      // Each type asks its own insurance question; an answer to the other one must not carry over.
      hasInsurance: null,
      insuranceChanged: null,
    });
    setProviders([]);
    goNext();
  };

  // Clear cached providers when template changes (e.g. user goes back and picks a different one)
  createEffect(
    on(
      () => formData().appointmentTemplateId,
      (_, prev) => {
        if (prev !== undefined) {
          setProviders([]);
        }
      },
    ),
  );

  // Animate step content on change — smooth height + fade
  let contentRef: HTMLDivElement | undefined;
  let prevHeight: number | null = null;

  // Capture height before step changes
  createEffect(
    on(
      () => step(),
      () => {
        if (contentRef) prevHeight = contentRef.offsetHeight;
      },
    ),
  );

  // After render, animate from old height → new height + fade in
  createEffect(
    on(
      () => step(),
      () => {
        const el = contentRef;
        if (!el) return;

        requestAnimationFrame(() => {
          const newHeight = el.offsetHeight;

          // Height transition
          if (prevHeight !== null && prevHeight !== newHeight) {
            el.animate(
              [
                { height: `${prevHeight}px`, overflow: "hidden" },
                { height: `${newHeight}px`, overflow: "hidden" },
              ],
              { duration: 250, easing: "ease-out" },
            );
          }

          // Fade-in + slide
          el.animate(
            [
              { opacity: 0, transform: "translateY(8px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            { duration: 200, easing: "ease-out" },
          );

          prevHeight = null;
        });
      },
      { defer: true },
    ),
  );

  const handleSubmit = async () => {
    if (refuseInTestMode()) return;
    setSubmitting(true);
    setSubmitErrors([]);

    try {
      const result = await submitSchedule(props.location.id, formData(), locale());

      if (result.success) {
        setConfirmation(result.confirmation);
        setStep("confirmation");
        // Fire booking conversion across every configured sink: in-iframe GTM/Meta
        // and the embedding parent's own GTM (see ./analytics).
        emitBookingComplete({
          bookingType: formData().patientType,
          bookingId: result.confirmation.appointmentId,
          embedOrigins: props.embedOrigins,
        });
      } else {
        setSubmitErrors(result.errors);
      }
    } catch {
      setSubmitErrors([t("network_error")]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PatientTextProvider value={patientText}>
      <I18nProvider value={t}>
        <Title>
          {props.location.name} — {t("schedule_appointment")}
        </Title>

        {/* Loads the location's GTM/Meta container once for the whole booking flow */}
        <TrackingPixels trackingPixels={props.trackingPixels} />

        <PublicFormShell
          width="md"
          poweredByLabel={t("powered_by")}
          toolbar={<PatientLanguageBar current={locale()} onPick={pickLanguage} />}
        >
          <Show when={props.trackingTest}>
            {(trackingTest) => (
              <TrackingTestStrip
                code={trackingTest().code}
                trackingPixels={props.trackingPixels}
                embedOrigins={props.embedOrigins}
              />
            )}
          </Show>
          <PublicFormHeader
            location={props.location}
            class="border-b border-brand-subtle bg-brand-light px-6 py-4"
          />
          <Show when={showsChrome()}>
            <div class="border-b border-brand-subtle px-6 pt-5 pb-4">
              <StepIndicator steps={stepsForFlow()} currentStep={step()} />
            </div>
          </Show>

          <Show when={selectionSummary().length > 0 && showsChrome()}>
            <div class="flex flex-col gap-1 border-b border-brand-subtle px-6 py-3">
              <For each={selectionSummary()}>
                {(row) => (
                  <div class="flex items-baseline gap-2">
                    <span class="text-xs text-typography-secondary">{row.label}</span>
                    <span class="text-xs font-medium text-typography-primary">{row.value}</span>
                  </div>
                )}
              </For>
            </div>
          </Show>

          <div ref={contentRef} class="min-h-[280px] px-6 py-6">
            <Switch>
              <Match when={step() === "patient_type"}>
                <PatientTypeStep onSelect={handlePatientTypeSelect} />
              </Match>

              <Match when={step() === "existing_patient_info"}>
                <PatientInfoStep
                  formData={formData()}
                  onUpdate={update}
                  onNext={goNext}
                  onBack={goBack}
                  isExistingPatient={true}
                  phoneVerificationEnabled={props.phoneVerificationEnabled}
                  locationId={props.location.id}
                  locale={locale()}
                />
              </Match>

              <Match when={step() === "appointment_info"}>
                <AppointmentInfoStep
                  templates={templates()}
                  formData={formData()}
                  onUpdate={update}
                  onNext={goNext}
                  onBack={goBack}
                />
              </Match>

              <Match when={step() === "provider_select"}>
                <ProviderStep
                  formData={formData()}
                  onUpdate={update}
                  onNext={goNext}
                  onBack={goBack}
                  locationId={props.location.id}
                  providers={providers()}
                  onProvidersLoaded={(p) => setProviders(p)}
                />
              </Match>

              <Match when={step() === "appointment_time"}>
                <AppointmentTimeStep
                  formData={formData()}
                  onUpdate={update}
                  onNext={goNext}
                  onBack={goBack}
                  locationId={props.location.id}
                  selectedTemplate={selectedTemplate()}
                  timezone={props.location.timezone}
                  locale={locale()}
                  // A test visit reserves no chair.
                  slotHoldsEnabled={props.slotHoldsEnabled && !props.trackingTest}
                />
              </Match>

              <Match when={step() === "patient_info"}>
                <PatientInfoStep
                  formData={formData()}
                  onUpdate={update}
                  onNext={goNext}
                  onBack={goBack}
                  isExistingPatient={false}
                  phoneVerificationEnabled={props.phoneVerificationEnabled}
                  locationId={props.location.id}
                  locale={locale()}
                  referralQuestion={props.referralQuestion}
                />
              </Match>

              <Match when={step() === "insurance_info"}>
                <InsuranceInfoStep
                  formData={formData()}
                  cardUploadEnabled={props.insuranceCardUploadEnabled}
                  onUpdate={update}
                  onNext={goNext}
                  onBack={goBack}
                />
              </Match>

              <Match when={step() === "review"}>
                <ReviewStep
                  formData={formData()}
                  insuranceInfoEnabled={props.insuranceInfoEnabled}
                  selectedTemplate={selectedTemplate()}
                  selectedProvider={selectedProvider()}
                  location={props.location}
                  locale={locale()}
                  depositAmountCents={depositCents()}
                  onUpdate={update}
                  onSubmit={depositCents() ? goToPayment : handleSubmit}
                  onBack={goBack}
                  submitting={submitting()}
                  errors={submitErrors()}
                />
              </Match>

              {/* Keyed on the amount as well as the step, so the amount reaches
                DepositStep already narrowed rather than asserted non-null. */}
              <Match when={step() === "deposit" && depositCents()}>
                {(cents) => (
                  <DepositStep
                    formData={formData()}
                    locationId={props.location.id}
                    publishableKey={props.stripePublishableKey}
                    cardFeePercentage={props.cardFeePercentage}
                    amountCents={cents()}
                    locale={locale()}
                    onPaid={handleSubmit}
                    onBack={goBack}
                    errors={submitErrors()}
                  />
                )}
              </Match>

              <Match when={step() === "confirmation"}>
                <ConfirmationStep
                  confirmation={confirmation()!}
                  location={props.location}
                  locale={locale()}
                />
              </Match>
            </Switch>
          </div>
        </PublicFormShell>
      </I18nProvider>
    </PatientTextProvider>
  );
}

/**
 * Tracking test mode: reports which of the location's tags loaded on this visit,
 * and sends the test conversion (the same events a booking fires, booking nothing).
 */
function TrackingTestStrip(props: {
  code: string;
  trackingPixels: TrackingPixelConfig[];
  embedOrigins: string[];
}) {
  const [conversion, setConversion] = createSignal<"idle" | "sending" | "sent" | "error">("idle");

  onMount(() => {
    const code = props.code;
    const pixels = props.trackingPixels;
    let timer: number | undefined;
    const reportVisit = () => {
      timer = window.setTimeout(() => {
        void reportTrackingTestVisit(
          code,
          pixels.map((pixel) => ({
            service: pixel.externalService,
            pixelId: pixel.pixelId,
            loaded: trackingPixelLoaded(pixel),
          })),
        );
      }, TAG_SETTLE_MS);
    };

    if (document.readyState === "complete") reportVisit();
    else window.addEventListener("load", reportVisit, { once: true });

    onCleanup(() => {
      window.removeEventListener("load", reportVisit);
      window.clearTimeout(timer);
    });
  });

  const sendConversion = async () => {
    setConversion("sending");
    emitBookingComplete({
      bookingType: "test",
      bookingId: `test-${props.code}`,
      embedOrigins: props.embedOrigins,
      test: true,
    });
    const loaded = (service: string) =>
      props.trackingPixels.some(
        (pixel) => pixel.externalService === service && trackingPixelLoaded(pixel),
      );
    const sinks: TestConversionSinks = {
      gtm: loaded(GOOGLE_TAG_MANAGER),
      meta: loaded(META_PIXEL),
      embedParent: embedParentReceives(props.embedOrigins),
    };
    try {
      setConversion((await reportTestConversion(props.code, sinks)) ? "sent" : "error");
    } catch {
      setConversion("error");
    }
  };

  return (
    <div class="flex flex-col gap-2 border-b border-brand-warning bg-brand-warning-light px-6 py-3">
      <div class="flex items-start gap-2 text-xs font-medium text-brand-warning-dark">
        <Icon name="beaker" size="sm" class="mt-px shrink-0" />
        <span>
          Tracking test {props.code} {SEPARATOR_DOT} nothing here books a real appointment
        </span>
      </div>
      <Show
        when={conversion() === "sent"}
        fallback={
          <div class="flex flex-col items-start gap-1">
            <Button
              variant="warning"
              size="small"
              leftIconName="paper-airplane"
              loading={conversion() === "sending"}
              disabled={conversion() === "sending"}
              onClick={() => void sendConversion()}
            >
              Send test conversion
            </Button>
            <Show when={conversion() === "error"}>
              <span class="text-xs text-brand-danger">
                Couldn't record the test conversion. Try again.
              </span>
            </Show>
          </div>
        }
      >
        <span class="flex items-center gap-1.5 text-sm font-medium text-brand-success-dark">
          <Icon name="check-circle" size="sm" />
          Test conversion sent
        </span>
      </Show>
    </div>
  );
}
